import React, {useEffect, useState} from 'react';
import {
    Box,
    Button,
    Checkbox,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    FormControlLabel,
    List,
    ListItem,
    Stack,
    Typography,
} from '@mui/material';
import {Consent} from "crqlar-react-common/dist/models/identity/consent.model";
import {useLocalize} from "crqlar-react-common/dist/utils/text.util";
import {useMutation} from "react-query";
import {AuthService} from "crqlar-react-common/dist/services/auth/auth.service";
import {LoadingButton} from "@mui/lab";
import {MyAccountService} from "crqlar-react-common/dist/services/identity/myAccount.service";
import {getIconForLanguage, getNameForLanguage} from "crqlar-react-common/dist/utils/translations.util";

interface ConsentDialogProps {
    open: boolean;
    consentsToAccept: Consent[];
    onAccept: () => void;
}

const ConsentDialog: React.FC<ConsentDialogProps> = ({
                                                         open,
                                                         consentsToAccept,
                                                         onAccept
                                                     }) => {

    const [acceptedConsents, setAcceptedConsents] = useState<Set<string>>(new Set());
    const [error, setError] = useState<string | null>(null);
    const {t, localize, i18n} = useLocalize();

    useEffect(() => {
        if (open && consentsToAccept) {
            // Reset state when dialog opens
            setAcceptedConsents(new Set());
            setError(null);
        }
    }, [open, consentsToAccept]);


    const handleToggleConsent = (consentSku: string) => {
        const newAcceptedConsents = new Set(acceptedConsents);
        if (newAcceptedConsents.has(consentSku)) {
            newAcceptedConsents.delete(consentSku);
        } else {
            newAcceptedConsents.add(consentSku);
        }
        setAcceptedConsents(newAcceptedConsents);
    };

    const handleAcceptAll = () => {
        if (consentsToAccept) {
            setAcceptedConsents(new Set(consentsToAccept.map(consent => consent.code)));
        }
    };

    const onChangeLanguage = (language: string) => {
        i18n.changeLanguage(language)
        MyAccountService.changeLanguage(language)
    }

    const acceptMut = useMutation(
        ['acceptConsents'],
        async (skus: string[]) => {
            await AuthService.acceptConsents(skus);
            onAccept()
        }
    )

    const handleSubmit = async () => {
        if (!consentsToAccept) return;

        const allConsentsAccepted = consentsToAccept.every(
            consent => acceptedConsents.has(consent.code)
        );

        if (!allConsentsAccepted) {
            setError(t('You must accept all consents to continue'));
            return;
        }

        try {
            await acceptMut.mutate(Array.from(acceptedConsents));
        } catch (err) {
            setError(t('An error occurred while saving your consent preferences'));
        }
    };

    const isAllAccepted = consentsToAccept?.every(
        consent => acceptedConsents.has(consent.code)
    );

    return (
        <Dialog
            open={open}
            onClose={undefined}
            maxWidth="md"
            fullWidth
            aria-labelledby="consent-dialog-title"
        >
            <DialogTitle id="consent-dialog-title">
                <Stack
                    direction={'row'}
                    justifyContent={'space-between'}
                    alignItems={'center'}
                >
                    {t('Consent required')}

                    <Stack
                        direction={'row'}
                        gap={1}
                    >
                        {
                            ['en', 'de', 'it'].map((option, index) => (
                                <Box
                                    key={index}
                                    onClick={() => {
                                        onChangeLanguage(option)
                                    }}
                                    sx={{
                                        cursor: 'pointer'
                                    }}
                                >
                                    <Stack
                                        direction={'row'}
                                        gap={1}
                                        alignItems={'center'}
                                    >
                                        <img
                                            alt={getNameForLanguage(option)}
                                            style={{width: '18px', height: '18px'}}
                                            src={getIconForLanguage(option)}
                                        />
                                    </Stack>

                                </Box>
                            ))
                        }
                    </Stack>
                </Stack>


            </DialogTitle>

            <DialogContent>
                <Typography variant="body1" paragraph>
                    {t('Please review and accept the following consents to continue')}:
                </Typography>

                {error && (
                    <Typography color="error" paragraph>
                        {error}
                    </Typography>
                )}

                <List>
                    {consentsToAccept?.map((consent) => (
                        <ListItem
                            key={consent.code}
                            divider
                            sx={{
                                pb: 2,  // padding bottom
                                pt: 2,  // padding top
                                '&:last-child': {
                                    mb: 0  // remove margin from last item
                                }
                            }}
                        >
                            <FormControlLabel
                                control={
                                    <Checkbox
                                        checked={acceptedConsents.has(consent.code)}
                                        onChange={() => handleToggleConsent(consent.code)}
                                        color="primary"
                                    />
                                }
                                label={
                                    <Box>
                                        <Typography
                                            variant="body1"
                                            dangerouslySetInnerHTML={{
                                                __html: localize(consent.message) || ''
                                            }}
                                        />
                                    </Box>
                                }
                            />
                        </ListItem>
                    ))}
                </List>
            </DialogContent>

            <DialogActions>
                <Button
                    onClick={handleAcceptAll}
                    disabled={isAllAccepted}
                >
                    {t('Accept all')}
                </Button>
                <LoadingButton
                    onClick={handleSubmit}
                    variant="contained"
                    color="primary"
                    disabled={!isAllAccepted}
                    loading={acceptMut.isLoading}
                >
                    {t('Continue')}
                </LoadingButton>
            </DialogActions>
        </Dialog>
    );
};

export default ConsentDialog;